import { replaceVerbatim } from "@project/shared/src/replaceVerbatim";
import type * as THREE from "three";

/**
 * Cell size, in pixels, at which a grid level starts to fade out.
 *
 * With the lines drawn at constant ink below, a level survives down to about
 * its own line width, so this only has to catch the point where neighbouring
 * lines merge into each other: at three pixels a cell, a one-pixel line has a
 * two-pixel gap beside it and is still a grid.
 */
const FADE_BEGINS_AT_CELL_PIXELS = 3;

/**
 * Cell size, in pixels, at which a grid level is gone.
 *
 * Two pixels per cell is the Nyquist limit and one and a half is past it: the
 * screen has no sample left to put between two lines, so what it shows is not
 * the grid but the beat between the line spacing and the pixel spacing.
 */
const FADE_ENDS_AT_CELL_PIXELS = 1.5;

const edge = (cellPixels: number): string => (1 / cellPixels).toFixed(4);

/**
 * How much of a grid level survives at a given density, per axis.
 *
 * The derivative of a coordinate measured in cells is how many cells one pixel
 * covers, so its reciprocal is the cell size in pixels — the one number that
 * says whether the screen can still resolve this level, at any camera distance
 * and any angle.
 */
const DENSITY_FADE = /* glsl */ `
vec2 gridDensityFade(vec2 cellsPerPixel) {
  return 1.0 - smoothstep(
    ${edge(FADE_BEGINS_AT_CELL_PIXELS)},
    ${edge(FADE_ENDS_AT_CELL_PIXELS)},
    cellsPerPixel
  );
}
`;

/**
 * drei's `getGrid`, replaced body and all.
 *
 * Three things change, and each of them is a separate cause of moiré:
 *
 * - **The line never gets thinner than a pixel.** drei asks for a line half a
 *   pixel wide and then tests whether the pixel *centre* landed inside it, so
 *   whether a line is drawn at all depends on where its sub-pixel phase fell.
 *   Lines a few pixels apart drift in and out of phase across the floor, and
 *   that drift is the interference pattern. Widening the line to one pixel and
 *   dividing its opacity by how much it was widened keeps the ink the same and
 *   takes the phase out of it — Emil Persson's phone-wire antialiasing, by way
 *   of Ben Golus's grid shader (CC0).
 * - **The density is measured as a length.** `fwidth` is `|dFdx| + |dFdy|`,
 *   which overstates a diagonal by up to √2, so drei's line width breathed as
 *   the camera turned. Per axis, the distance one pixel travels along that
 *   axis is `length(vec2(dFdx(r).x, dFdy(r).x))`.
 * - **A level stops being drawn once it is too fine to read**, rather than
 *   being sampled below Nyquist for the rest of the floor. The next level up
 *   is ten times coarser and still has tens of pixels per cell when the finer
 *   one goes, so there is a grid at every distance.
 *
 * The two axes are kept apart until the end: looking along the floor squeezes
 * the lines that run across the view long before it touches the ones running
 * away from it, and drei's `min(grid.x, grid.y)` would drop both together.
 * They compose as coverage, `x + y - xy`, which is what drei's minimum stood
 * in for everywhere except the crossings.
 *
 * `thickness` stays what drei documents it as, a line width in pixels, so the
 * grids in `GridFloorScene` keep their relative weights.
 */
const GET_GRID = /* glsl */ `
float getGrid(float size, float thickness) {
  // A level switched off by giving it no size: dividing by 0.0 is an infinity,
  // and fract() of one is whatever the driver feels like.
  if (size <= 0.0) return 0.0;

  vec2 r = localPosition.xz / size;
  vec2 ddxR = dFdx(r);
  vec2 ddyR = dFdy(r);
  vec2 cellsPerPixel = vec2(
    length(vec2(ddxR.x, ddyR.x)),
    length(vec2(ddxR.y, ddyR.y))
  );

  // Distance from the nearest line, in half cells: 0 on a line, 1 mid cell.
  vec2 gridUv = 1.0 - abs(fract(r) * 2.0 - 1.0);

  // Never thinner than a pixel, never wider than half a cell — and the lower
  // bound has to be held under the upper one itself, since a cell smaller than
  // two pixels would otherwise hand clamp() a range that runs backwards, which
  // GLSL leaves undefined.
  vec2 wantedWidth = thickness * cellsPerPixel;
  vec2 drawnWidth = clamp(
    wantedWidth,
    min(cellsPerPixel, vec2(0.5)),
    vec2(0.5)
  );
  // Ascending edges and a floor under both widths. A descending smoothstep is
  // undefined in GLSL exactly as a backwards clamp is, and a pixel whose
  // derivatives vanish — a quad seen edge on, or the far field of an infinite
  // grid, where the scaled coordinate quantises — makes both the smoothstep's
  // two edges and the division below degenerate at once.
  vec2 lineAa = max(cellsPerPixel * 1.5, vec2(1e-6));
  vec2 safeWidth = max(drawnWidth, vec2(1e-6));
  vec2 coverage =
    1.0 - smoothstep(safeWidth - lineAa, safeWidth + lineAa, gridUv);
  coverage *= clamp(wantedWidth / safeWidth, 0.0, 1.0);
  coverage *= gridDensityFade(cellsPerPixel);

  return mix(coverage.x, 1.0, coverage.y);
}
`;

/**
 * The whole of drei's `getGrid`, as the version in the lockfile writes it.
 */
const DREI_GET_GRID = `float getGrid(float size, float thickness) {
      vec2 r = localPosition.xz / size;
      vec2 grid = abs(fract(r - 0.5) - 0.5) / fwidth(r);
      float line = min(grid.x, grid.y) + 1.0 - thickness;
      return 1.0 - min(line, 1.0);
    }`;

/**
 * Draw drei's grid without moiré.
 *
 * The floor here is metres and the finest cell is a centimetre drawn out to
 * twenty of them, so the grid is under-sampled across most of that quad, and
 * a line that is only tested at pixel centres beats against the pixel grid
 * long before that. Neither is something antialiasing can undo afterwards —
 * MSAA included — because the signal is gone before it is rasterised. The
 * filtering has to happen where the lines are generated, which is
 * {@link GET_GRID}.
 *
 * A patch rather than a fork: drei's material is not exported, so the shader
 * is only reachable through `onBeforeCompile`. The replacement is checked,
 * since a `replace` that silently missed would put the moiré straight back.
 */
export const fadeGridByPixelDensity = (shader: {
  fragmentShader: string;
}): void => {
  if (!shader.fragmentShader.includes(DREI_GET_GRID)) {
    throw new Error("Grid shader does not contain drei's getGrid to replace");
  }
  shader.fragmentShader = replaceVerbatim(
    shader.fragmentShader,
    DREI_GET_GRID,
    `${DENSITY_FADE}\n${GET_GRID}`,
  );
};

/**
 * Program cache key for the patched grid.
 *
 * Three's program cache key is built from the shader sources, the defines and
 * `customProgramCacheKey()` — never from what `onBeforeCompile` wrote — and
 * the default implementation of that method stringifies `onBeforeCompile`,
 * which is what keeps a patched grid out of an unpatched one's cache slot. A
 * name says the same thing without stringifying a closure on every material
 * update, and says it whatever shape the installed hook happens to have.
 */
const GRID_PROGRAM_KEY = "grid-pixel-density-fade";

/**
 * Put {@link fadeGridByPixelDensity} on the material of a drei `<Grid>`.
 *
 * Three compiles a material the first time it draws, which is after the effect
 * that calls this, but `needsUpdate` — together with the cache key above —
 * also covers a grid that was drawn before its ref was attached.
 *
 * The patch is installed behind a catch. `onBeforeCompile` runs inside
 * `WebGLRenderer.render`, so a drei release that reflows `getGrid` would
 * otherwise throw out of the render loop and take the whole canvas with it;
 * swallowed, the floor falls back to drei's own grid and the worst that
 * happens is the moiré this module exists to remove. The throw itself stays,
 * because that is what the test asserts against the installed package.
 */
export const applyGridPixelDensityFade = (mesh: THREE.Mesh | null): void => {
  const material = mesh?.material;
  if (material === undefined || Array.isArray(material)) return;
  material.onBeforeCompile = (shader) => {
    try {
      fadeGridByPixelDensity(shader);
    } catch (error) {
      console.warn("Grid pixel-density fade not applied", error);
    }
  };
  // Constant, and deliberately not derived from whether the patch took: three
  // asks for the key before it calls `onBeforeCompile`, and every grid in the
  // scene goes through this one function, so they either are all patched or
  // all are not.
  material.customProgramCacheKey = () => GRID_PROGRAM_KEY;
  material.needsUpdate = true;
};
